import React, { useEffect, useState, useMemo } from 'react';
import { Button, Col, Row, Tooltip, Modal, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { DeleteOutlined, ExclamationCircleOutlined, FormOutlined } from '@ant-design/icons';
import UpdateSensorDrawer from '../UpdateSensorDrawer/UpdateSensorDrawer';
import AddSensorModal from '../AddSensorModal/AddSensorModal';
import AppTable from '../../../../components/AppTable/AppTable';
import { Sensor } from '../../../../redux/reducers/clients/clients.types';
import { fetchSensorsRequest, fetchGatewaysRequest, deleteSensorRequest } from '../../../../redux/reducers/clients/clientsSlice';
import { RootState } from '../../../../redux/store';
import { colors } from '../../../../utils/colors/colors';

interface SensorsTableProps {
    clientId: string;
}

const SensorsTable: React.FC<SensorsTableProps> = ({ clientId }) => {
    const { t } = useTranslation('clients');
    const dispatch = useDispatch();

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedSensorId, setSelectedSensorId] = useState<string | undefined>();

    const { data: sensors, loading: sensorsLoading } = useSelector((state: RootState) => state.clients.sensors);
    const { data: gateways } = useSelector((state: RootState) => state.clients.gateways);

    const gatewayMap = useMemo(() => new Map((gateways || []).map(g => [g.id, g])), [gateways]);

    const handleEditSensor = (sensorId: string) => {
        setSelectedSensorId(sensorId);
        setIsDrawerOpen(true);
    };

    const handleDeleteSensor = (sensorId: string) => {
        Modal.confirm({
            title: t('are_you_sure_delete_sensor'),
            icon: <ExclamationCircleOutlined />,
            okText: t('delete'),
            cancelText: t('cancel'),
            onOk: () => dispatch(deleteSensorRequest({ sensorId })),
        });
    };

    const columns = [
        {
            title: <span className="base-table-text">{t("name")}</span>,
            dataIndex: 'name',
            key: 'name'
        },
        {
            title: <span className="base-table-text">{t("serial_number")}</span>,
            dataIndex: 'serialno',
            key: 'serialno'
        },
        {
            title: <span className="base-table-text">{t("linked_gateway")}</span>,
            dataIndex: 'gatewayId',
            key: 'gatewayId',
            render: (gatewayId: string) => {
                const gateway = gatewayMap.get(gatewayId);
                return gateway ? `${gateway.gateway} (${gateway.gsm})` : t('unlinked');
            },
        },
        {
            title: <span className="base-table-text">{t("last_record_date")}</span>,
            dataIndex: 'lastRecordDate',
            key: 'lastRecordDate',
        },
        {
            title: <span className="base-table-text">{t("battery")}</span>,
            dataIndex: 'battery',
            key: 'battery',
        },
        {
            title: <span className="base-table-text">{t("actions")}</span>,
            key: 'actions',
            align: 'center' as const,
            render: (_: any, record: Sensor) => (
                <Row justify="center" gutter={16}>
                    <Col>
                        <Tooltip title={t('edit')}><Button type="text" icon={<FormOutlined />} onClick={() => handleEditSensor(record.id)} /></Tooltip>
                    </Col>
                    <Col>
                        <Tooltip title={t('delete')}><Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDeleteSensor(record.id)} /></Tooltip>
                    </Col>
                </Row>
            ),
        },
    ];

    useEffect(() => {
        if (clientId) {
            dispatch(fetchSensorsRequest(clientId));
            dispatch(fetchGatewaysRequest(clientId));
        }
    }, [dispatch, clientId]);

    return (
        <>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                <Col>
                    <Typography.Title style={{ margin: 0 }} level={3}>{t('sensors')}</Typography.Title>
                </Col>
                <Col>
                    <Button type="primary" style={{ backgroundColor: '#02b9d9' }} onClick={() => setIsModalOpen(true)}>
                        {t('add_sensor')}
                    </Button>
                </Col>
            </Row>
            <AppTable
                className='base-table-body'
                loading={sensorsLoading}
                dataSource={sensors}
                columns={columns}
                paginationConfig={{
                    pageSize: 10,
                    showSizeChanger: false,
                }}
            />
            <UpdateSensorDrawer
                open={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                sensorId={selectedSensorId}
                clientId={clientId}
            />
            <AddSensorModal
                visible={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                clientId={clientId}
            />
        </>
    );
};

export default SensorsTable;